

import { useContext, useState } from 'react';
import {
  getAuth,
  signInWithEmailAndPassword,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithPopup,
  signInWithPhoneNumber,
  GoogleAuthProvider,
  FacebookAuthProvider,
  TwitterAuthProvider,
  RecaptchaVerifier,
  signOut,
  createUserWithEmailAndPassword
} from 'firebase/auth';

import { ParentContext } from '../ParentContext';
import { _collectionName_appUserValidation, _emailSignInStorageItem } from 'global/cnr/reducers/ValidationReducer';
import { createRefPath, createRefPath_client } from 'global/firestoreData/appData/appRefPaths';
import { serverTimestamp, writeBatch } from 'firebase/firestore';
import { gEnums } from 'global/enums/globalEnums';
import { createInitials } from 'global/common/dataAdjust';
import { DatabaseContext } from '../DatabaseContext';
import { AppContext } from '../AppProvider';
import _ from 'lodash';

export function useSignIn() {

  const appContext = useContext(AppContext)
  const { app_state } = appContext ?? {}
  const { mainApp, mainAuth, mainDb, app_ts } = app_state

  const databaseContext = useContext(DatabaseContext)
  const { database_fns } = databaseContext ?? {}

  const parentContext = useContext(ParentContext);
  const { states } = parentContext ?? {}
  const { paps_state } = states ?? {}
  const { pathViews } = paps_state ?? {}

  let _currentApp = mainApp
  let _currentAuth = mainAuth
  let _currentDb = mainDb

  const _host = document.location.host
  let appName = 'ME'

  switch (_host) {
    case 'thumbstat.com':
      _currentApp = app_ts.app
      _currentDb = app_ts.db
      appName = 'thumbstat'
      // _currentAuth = app_ts.auth
      break;
    default:
    // _currentApp = app_ts.app
    // _currentAuth = app_ts.auth
    // nothing
  }

  const auth_current = getAuth(_currentApp);

  const [user, setUser] = useState(auth_current.currentUser);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [notification, setNotification] = useState();

  const createAccount = async (creds) => {
    const { email, password } = creds ?? {}
    if (!email || !password) {
      setNotification({
        title: 'No Email/Password',
        content: `You have not provided an email/password.`,
      })
      return;
    }
    setLoading(true);
    try {
      const user = await createUserWithEmailAndPassword(
        auth_current,
        email,
        password
      )
      setUser(user);
      setError(null);
      setNotification({
        title: 'Success',
        content: `You are now signed in with the email: ${email}.`,
      })
      const cb = () => {

      }
      createAccount_db(database_fns, user, pathViews, cb)
    } catch (err) {
      setError(err);
      setNotification({
        title: 'Create Account unsuccessfull',
        content: err.message,
      })
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Email & Password
  // -----------------------------
  const emailPasswordSignIn = async (creds) => {
    const { email, password } = creds ?? {}
    if (!email || !password) {
      setNotification({
        title: 'No Email/Password',
        content: `You have not provided an email/password.`,
      })
      return;
    }
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(mainAuth, email, password);
      setUser(cred.user);
      setError(null);
      setNotification({
        title: 'Success',
        content: `You are now logged in with the email: ${email}.`,
      })
      return cred.user;
    } catch (err) {
      setError(err);
      setNotification({
        title: 'Login unsuccessfull',
        content: `You are not logged in with the email: ${email}.`,
      })
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Email Link (Passwordless)
  // -----------------------------
  const sendEmailLink = async (creds) => {
    const { email, options } = creds ?? {}
    if (!email) {
      setNotification({
        title: 'No Email',
        content: `You have not provided an email.`,
      })
      return;
    }
    setLoading(true);
    try {
      const params = {
        isEmailLink: true,
        emailSignInConfirmation: true,
        openInSamePWA: true,
        email,
      }
      const currentUrl = window.location.href
      var actionCodeSettings = {
        url: currentUrl,
        handleCodeInApp: true,
        appName: 'TEST'
      };
      if (actionCodeSettings.url) {
        Object.keys(params).forEach(paramName => {
          actionCodeSettings.url += `${actionCodeSettings.url.includes('?') ? '&' : '?'}${paramName}=${params[paramName]}`;
        });
      }
      await sendSignInLinkToEmail(auth_current, email, actionCodeSettings);

      window.localStorage.setItem(_emailSignInStorageItem, email);
      window.localStorage.setItem(`${_emailSignInStorageItem}_options`, JSON.stringify(options));

      console.log('options', options)
      setNotification({
        title: 'Email has been sent',
        content: `An email has been sent to ${email}.`,
      })
      setError(null);
    } catch (err) {
      setError(err);
      setNotification({
        title: `Email has NOT been sent to ${email}`,
        content: err.message,
      })
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const completeEmailLinkSignIn = async (email) => {
    setLoading(true);
    try {
      if (isSignInWithEmailLink(auth_current, window.location.href)) {
        if (email) {
          const result = await signInWithEmailLink(auth_current, email, window.location.href);
          console.log('result', result)
          const user = result?.user;
          setUser(user);
          const options = window.localStorage.getItem(`${_emailSignInStorageItem}_options`);
          window.localStorage.removeItem(_emailSignInStorageItem);
          window.localStorage.removeItem(`${_emailSignInStorageItem}_options`);
          setError(null);
          setSuccess(true);
          setNotification({
            title: `Email Confirmation`,
            content: 'You are now signed in to the app. You can safely close this page.',
          })
          appUserValidation_update(_currentDb, database_fns, email, gEnums.emailValidationTypes.activated, null, options)
        } else {
          setSuccess(false);
          setNotification({
            title: `No email found`,
            content: 'No email found during validation.',
          })
        }
        return user;
      }
    } catch (err) {
      setError(err);
      setSuccess(false);
      setNotification({
        title: `There was an issue with the confirmation link to ${email}`,
        content: err.message,
      })
    } finally {
      setLoading(false);
    }
  };

  const isSignInEmailLink = async (url) => {
    return isSignInWithEmailLink(auth_current, url)
  }

  // -----------------------------
  // Popup Providers (Google, Facebook, Twitter)
  // -----------------------------
  const popupSignIn = async (providerName) => {
    setLoading(true);
    try {
      let provider;
      switch (providerName) {
        case 'google': provider = new GoogleAuthProvider(); break;
        case 'facebook': provider = new FacebookAuthProvider(); break;
        case 'twitter': provider = new TwitterAuthProvider(); break;
        default: throw new Error('Unknown provider');
      }
      const cred = await signInWithPopup(auth_current, provider);
      setUser(cred.user);
      setError(null);
      setNotification({
        title: 'Success',
        content: `You are now signed in in with ${providerName}`,
      })
      return cred.user;
    } catch (err) {
      setError(err);
      setNotification({
        title: `There was an signing in with to your ${providerName} account`,
        content: err.message,
      })
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Phone Number Sign-In
  // -----------------------------
  const phoneSignIn = async (phoneNumber, verifier) => {
    setLoading(true);
    try {
      const confirmationResult = await signInWithPhoneNumber(auth_current, phoneNumber, verifier);
      setError(null);
      setNotification({
        title: 'Success',
        content: `You are now signed in in with you phone number ${phoneNumber}`,
      })
      return confirmationResult; // confirmationResult.confirm(code) later
    } catch (err) {
      setError(err);
      setNotification({
        title: `There was an issue signing in with your phone number: ${phoneNumber}`,
        content: err.message,
      })
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Sign out
  // -----------------------------
  const signOutUser = async () => {
    try {
      await signOut(auth_current);
      setUser(null);
    } catch (err) {
      setError(err);
      console.error(err);
    }
  };

  return {
    auth: auth_current,
    completeEmailLinkSignIn,
    createAccount,
    emailPasswordSignIn,
    error,
    notification,
    phoneSignIn,
    popupSignIn,
    sendEmailLink,
    isSignInEmailLink,
    signOutUser,
    success,
    updating: loading,
    user,
    appName
  };
}

const appUserValidation_update = async (db, database_fns, email, status, response, options) => {

  const _refPath = createRefPath([_collectionName_appUserValidation, email])
  const _ts = serverTimestamp()

  let dataToUpdate;
  let dataToSet;

  switch (status) {
    case gEnums.emailValidationTypes.requested:
      if (response) {
        const { result } = response ?? {}
        const { link } = result ?? {}
        var urlParams = new URLSearchParams(link);
        var oobCode = _.get(Object.fromEntries(urlParams), 'oobCode');
        dataToSet = { email, status, oobCode, link, pendingTimestamp: _ts }
      }
      break;
    case gEnums.emailValidationTypes.requestedNonLink:
      dataToSet = { email, status, pendingTimestamp: _ts }
      break;
    case gEnums.emailValidationTypes.accepted:
      dataToUpdate = { status, acceptedTimestamp: _ts }
      break;
    case gEnums.emailValidationTypes.denied:
      dataToUpdate = { status, deniedTimestamp: _ts }
      break;
    default:
      dataToUpdate = { status }
      if (options) {
        dataToUpdate.options = JSON.parse(options);
      }
    // nothing
  }

  const batch = writeBatch(db);

  if (dataToSet) {
    database_fns.set_doc(_refPath, dataToSet, true, false, batch)
  } else if (dataToUpdate) {
    console.log('_refPath', _refPath, dataToUpdate)
    database_fns.update_doc(_refPath, dataToUpdate, null, batch)
  }

  batch.commit()
}

const createAccount_db = (database_fns, currentUser, pathViews, callback) => {
  const { email: email_current } = currentUser ?? {}
  if (email_current === email) {
    const dataToUpdate = {
      email: email,
      firstName,
      lastName,
      displayName: firstName + ' ' + lastName,
      initials: createInitials(firstName + ' ' + lastName)
    }
    switch (createProfileType) {
      case gEnums.createProfileTypes.clientProfiles:
        const _refPath_c = createRefPath_client(pathViews, ['profiles', email])
        database_fns.set_doc(_refPath_c, dataToUpdate, null, callback)
        break;
      case gEnums.createProfileTypes.profiles:
        const _refPath_p = createRefPath('users')
        database_fns.add_doc(_refPath_p, dataToUpdate, callback)
        break;
      default:
      // nothing
    }
  }
}